import { Component, OnDestroy, ViewChild, ElementRef, OnInit, NgZone, Input } from "@angular/core";
import { SafeResourceUrl, DomSanitizer } from "@angular/platform-browser";
import { IFrameComponent } from "iframe-resizer";
import { Store, select } from "@ngrx/store";
import iFrameResize from "iframe-resizer/js/iframeResizer";
import { IAppState } from "@edp-store/state/app.state";
import { ActivatedRoute, Router } from "@angular/router";
import { getIframe } from "@edp-store/selectors/iframe.selectors";
import { environment } from "@edp-environments/environment";
import { DeviceDetectorService } from "ngx-device-detector";
import { getContract, getContracts } from "@edp-store/selectors/contract.selectors";
import { Iframe } from "@edp-pages/iframe/iframe.model";
import { ResetIframe } from "@edp-store/actions/iframe.actions";
import { filter, take, withLatestFrom, switchMap } from "rxjs/operators";
import { Contract } from "@edp-components/edp-contract";
import { of, EMPTY } from "rxjs";
import { CookieService } from "ngx-cookie-service";
import { getUser } from "@edp-store/selectors/user.selectors";
import { AppService } from "@edp-services/app.service";
import { getContractEs, getContractsEs } from "@edp-store/selectors/ES/contract-es.selectors";
import { IAppEsState } from "@edp-store/state/ES/app-es.state";
import { RegionService } from "@edp-services/region.service";
import { RegionEnum } from "@edp-enums/region.enum";
import { EnvService } from "@edp-services/env.service";

@Component({
    selector: "edp-iframe",
    templateUrl: "./edp-iframe.component.html",
})
export class EdpIframeComponent implements OnInit, OnDestroy {
    @Input() showHeader = true;
    @Input() defaultHeight: string;
    @Input() isMgmIframe = false;
    public isApp: boolean;
    @ViewChild("iframeElement", { read: ElementRef, static: false }) set content(iframeElement: ElementRef) {
        this.iframeElement = iframeElement;
        this.initIframeResizer();
    }

    public urlSafe: SafeResourceUrl;

    public contract$ = this.store.pipe(select(getContract));
    public contractES$ = this.storeES.pipe(select(getContractEs));

    private loaded = false;
    private component: IFrameComponent;
    private iframeElement: ElementRef;
    private frameWidth: number;

    constructor(
        public sanitizer: DomSanitizer,
        public store: Store<IAppState>,
        public storeES: Store<IAppEsState>,
        public router: Router,
        public route: ActivatedRoute,
        public deviceDetector: DeviceDetectorService,
        public cookieService: CookieService,
        public appService: AppService,
        public ngZone: NgZone,
        public regionService: RegionService,
        private envService: EnvService
    ) {}

    ngOnInit() {
        this.isApp = environment.isApp;

        this.store.select(getIframe).subscribe(data => {
            try {
                const iframeUrl: string = this.iframeElement.nativeElement.contentWindow.location.href;
                if (iframeUrl.indexOf(this.envService.url()) < 0) {
                    this.loaded = false;
                }
            } catch (ex) {
                this.loaded = false;
            }
            if (data && data.route && !data.preload && this.loaded) {
                this.iframeElement.nativeElement.contentWindow.postMessage(JSON.stringify(data.route), location.origin);

                if (!!this.defaultHeight) {
                    this.iframeElement.nativeElement.style.maxHeight = this.defaultHeight + "px";
                }

                window.scroll(0, 0);
            } else if (data && data.route && (!this.loaded || data.reload)) {
                /* this.openUrl(data.route.url); */
            }
        });

        this.checkContracts();

        this.frameWidth = Math.max(
            document.body.scrollWidth,
            document.documentElement.scrollWidth,
            document.body.offsetWidth,
            document.documentElement.offsetWidth,
            document.documentElement.clientWidth
        );
    }

    private checkContracts() {
        if (this.regionService.getRegion() !== RegionEnum.ES) {
            this.store
                .select(getContracts)
                .pipe(
                    filter(contracts => contracts.length > 0),
                    take(1),
                    withLatestFrom(this.store.select(getIframe)),
                    switchMap(([contracts, iframe]: [Contract[], Iframe]) => {
                        if (iframe.route && (iframe.route.url || iframe.route.route)) {
                            return EMPTY;
                        } else {
                            return of(contracts);
                        }
                    })
                )
                .subscribe(contracts => {
                    if (contracts && contracts.length) {
                        this.store.dispatch(new ResetIframe());
                    }
                });
            return;
        }

        this.storeES
            .select(getContractsEs)
            .pipe(
                filter(contracts => contracts.length > 0),
                take(1),
                withLatestFrom(this.store.select(getIframe)),
                switchMap(([contracts, iframe]) => {
                    if (iframe.route && (iframe.route.url || iframe.route.route)) {
                        return EMPTY;
                    } else {
                        return of(contracts);
                    }
                })
            )
            .subscribe(contracts => {
                if (contracts && contracts.length) {
                    this.store.dispatch(new ResetIframe());
                }
            });
    }

    private initIframeResizer() {
        if (this.component) {
            return;
        }

        const iframes = iFrameResize(
            {
                log: false,
                checkOrigin: false,
                heightCalculationMethod: "documentElementOffset",
                onResized: () => {
                    const body = this.iframeElement.nativeElement.contentWindow.document.body;
                    const html = this.iframeElement.nativeElement.contentWindow.document.documentElement;

                    let height = Math.max(body.scrollHeight, body.offsetHeight, html.clientHeight, html.scrollHeight, html.offsetHeight);

                    // T                                    \`.    T
                    // |    T     .--------------.___________) \   |    T
                    // !    |     |//////////////|___________[ ]   !  T |
                    //      !     `--------------'           ) (      | !
                    //                                       '-'      !
                    if (html.className.indexOf("modal") >= 0) {
                        height += 200;
                    }

                    // Exception when we only have a modal window in DOM
                    if (height === 0) {
                        height = 1000;
                    }

                    this.iframeElement.nativeElement.style.height = height + "px";

                    if (!!this.defaultHeight) {
                        this.iframeElement.nativeElement.style.maxHeight = this.defaultHeight + "px";
                    }

                    if (environment.isApp) {
                        this.iframeElement.nativeElement.style.width = !this.isMgmIframe ? this.frameWidth + "px" : "100%";
                    }
                },
                onMessage: iframe => {
                    if (iframe.message) {
                        try {
                            const data = JSON.parse(iframe.message);

                            switch (data.action) {
                                case "changeRoute":
                                    if (data.params && data.params.url) {
                                        this.ngZone.run(() => this.router.navigate([data.params.url]));
                                    }
                                    break;
                                case "changeRouteFocus":
                                    if (data.params && data.params.url) {
                                        this.ngZone.run(() => {
                                            this.router.navigate([data.params.url], {
                                                state: {
                                                    element: data.params.element,
                                                    extra: data.params.extra,
                                                },
                                            });
                                        });
                                    }
                                    break;
                                case "loaded":
                                    this.loaded = true;
                                    break;
                                case "error":
                                    this.loaded = false;
                                    this.ngZone.run(() => this.router.navigate(["dashboard"]));
                                    break;
                                case "download":
                                    if (environment.isApp) {
                                        this.appService.downloadFile(data.params.url);
                                    } else {
                                        const win = window.open(data.params.url, "_blank");
                                        win.focus();
                                    }
                                    break;
                            }
                        } catch (e) {}
                    }
                },
            },
            "#iframeElement"
        );

        this.component = iframes && iframes.length > 0 ? iframes[0] : null;
    }

    private openUrl(url: string) {
        url = `${this.envService.url()}/${url}`;

        const params = this.route.snapshot.queryParams;
        const query = [];

        for (const key in params) {
            if (params.hasOwnProperty(key)) {
                query.push(`${key}=${encodeURIComponent(params[key])}`);
            }
        }

        query.push("iframe=1");

        if (this.deviceDetector.isMobile()) {
            query.push("Mobile=1");
        }

        if (environment.isApp) {
            query.push("app=1");
        }

        if (sessionStorage.getItem("edponline-query")) {
            const edpQuery = sessionStorage.getItem("edponline-query");
            query.push(`${edpQuery.slice(1)}`);
        }

        this.store
            .select(getUser)
            .pipe(take(1))
            .subscribe(user => {
                console.log("user", user);
                let tokenType = "token";

                if (user.isImpersonate) {
                    tokenType = "impersonate";
                }

                console.log("tokenType", tokenType);
                const testCookie = this.cookieService.get("firebase_token");
                console.log("this.cookieService.get(firebase_token)", testCookie);

                query.push(`${tokenType}=${encodeURIComponent(this.cookieService.get("firebase_token"))}`);

                console.log("query", query);

                if (query.length > 0) {
                    url += "?" + query.join("&");
                }
                console.log("query", query);

                this.urlSafe = this.sanitizer.bypassSecurityTrustResourceUrl(url);

                console.log("urlSafe", this.urlSafe);
            });
    }

    ngOnDestroy() {
        if (this.component && this.component.iFrameResizer) {
            this.component.iFrameResizer.close();
        }
    }
}
